/**
 * Word frequency — pure, deterministic word counting.
 * Safe to render server-side.
 */

export interface FreqOptions {
  /** Treat words case-insensitively (default true). */
  caseInsensitive?: boolean;
  /** Ignore words shorter than this (default 1). */
  minLength?: number;
  /** Keep only the top N most frequent words (0 / undefined = all). */
  topN?: number;
}

export interface FreqItem {
  word: string;
  count: number;
}

export interface FreqResult {
  items: FreqItem[];
  /** Total counted words (after the minLength filter). */
  total: number;
  /** Distinct words. */
  unique: number;
}

export function wordFrequency(
  text: string,
  opts: FreqOptions = {},
): FreqResult {
  const caseInsensitive = opts.caseInsensitive ?? true;
  const minLength = opts.minLength ?? 1;

  const tokens = text
    .split(/[^\p{L}\p{N}]+/u)
    .filter((t) => t.length >= minLength);

  const map = new Map<string, number>();
  for (const tok of tokens) {
    const key = caseInsensitive ? tok.toLowerCase() : tok;
    map.set(key, (map.get(key) ?? 0) + 1);
  }

  const items: FreqItem[] = [...map.entries()].map(([word, count]) => ({
    word,
    count,
  }));
  items.sort((a, b) => b.count - a.count || a.word.localeCompare(b.word));

  const result: FreqResult = {
    items,
    total: tokens.length,
    unique: items.length,
  };

  if (opts.topN && opts.topN > 0) {
    result.items = items.slice(0, opts.topN);
  }
  return result;
}
